import {Inject, Injectable} from '@angular/core';
import {Observable, of, ReplaySubject} from "rxjs";
import {CountedAndPaginated} from "../interface/response/counted-and-paginated";
import {HttpClient} from "@angular/common/http";
import {FilterService} from "./filter.service";
import {DOCUMENT} from "@angular/common";
import {first, map, switchMap, take, tap} from "rxjs/operators";
import {DobryAgentBlogArticle, DobryAgentBlogTag} from "../interface/dobry-agent-blog-article";
import {Uuid} from "../interface/response/uuid";
import {ListResponse} from "../interface/response/list-response";
import {ConfirmDialogComponent} from "../components/confirm-dialog/confirm-dialog.component";
import {MatSnackBar} from "@angular/material/snack-bar";
import {MatDialog} from "@angular/material/dialog";

@Injectable({
  providedIn: 'root'
})
export class DobryAgentBlogService {
  public readonly blogArticleListSubject: ReplaySubject<CountedAndPaginated<DobryAgentBlogArticle>>
    = new ReplaySubject<CountedAndPaginated<DobryAgentBlogArticle>>(1);
  public readonly dobryAgentBlogArticleNotCountedListSubject: ReplaySubject<ListResponse<DobryAgentBlogArticle>> = new ReplaySubject<ListResponse<DobryAgentBlogArticle>>(1);

  constructor(
    private httpClient: HttpClient,
    private filterService: FilterService,
    @Inject(DOCUMENT) private document: Document,
    private snack: MatSnackBar,
    private dialog: MatDialog
  ) {
  }

  public loadList(page: number, pageSize: number, filters: object, sort?: { sortBy?: string; sortOrder?: string}): void {
    this.httpClient.get(`/rest/dobryagent/api/internal/blog-article?page=${page + 1}&pageSize=${pageSize}&sortBy=${sort.sortBy ?? ''}&sortOrder=${sort.sortOrder ?? ''}&` + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: CountedAndPaginated<DobryAgentBlogArticle>) => {
        this.blogArticleListSubject.next(result);
      });
  }

  public getBlogArticleListSubject(): ReplaySubject<CountedAndPaginated<DobryAgentBlogArticle>> {
    return this.blogArticleListSubject;
  }

  public get(uuid: string): Observable<DobryAgentBlogArticle> {
    return this.httpClient
      .get<DobryAgentBlogArticle>(`/rest/dobryagent/api/internal/blog-article/${uuid}`)
      .pipe(take(1));
  }

  public send(formData: object, uuid?: string): Observable<Uuid> {
    if (uuid) {
      return this.httpClient
        .put<Uuid>(`/rest/dobryagent/api/internal/blog-article/${uuid}`, formData)
        .pipe(take(1));
    }

    return this.httpClient
      .post<Uuid>(`/rest/dobryagent/api/internal/blog-article`, formData)
      .pipe(take(1));
  }

  public uploadImage(file: File, blogArticleUuid: string): Observable<any> {
    const formData = new FormData();
    formData.append('content', file);

    return this.httpClient
      .post(
        `/rest/dobryagent/api/internal/blog-article/${blogArticleUuid}/image-upload`,
        formData
      )
      .pipe(take(1));
  }

  public searchList(
    page: number,
    pageSize: number,
    filters: object = {},
    sort: { sortBy?: string; sortOrder?: string } = {}
  ): Observable<CountedAndPaginated<DobryAgentBlogArticle>> {
    const query = this.filterService.parseFilters(filters);

    return this.httpClient
      .get<CountedAndPaginated<DobryAgentBlogArticle>>(
        `/rest/dobryagent/api/internal/blog-article` +
        `?page=${page + 1}` +
        `&pageSize=${pageSize}` +
        `&sortBy=${sort.sortBy ?? ''}` +
        `&sortOrder=${sort.sortOrder ?? ''}` +
        `&${query}`
      )
      .pipe(take(1));
  }


  private delete(uuid: string): Observable<void> {
    return this.httpClient
      .delete<void>(
        `/rest/dobryagent/api/internal/blog-article/${uuid}/delete`
      )
      .pipe(first());
  }

  private deleteWeak(uuid: string): Observable<void> {
    return this.httpClient
      .patch<void>(
        `/rest/dobryagent/api/internal/blog-article/${uuid}`,
        {}
      )
      .pipe(first());
  }


  private restore(uuid: string): Observable<void> {
    return this.httpClient
      .patch<void>(
        `/rest/dobryagent/api/internal/blog-article/${uuid}/restore`,
        {}
      )
      .pipe(first());
  }

  public confirmDelete(
    dobryAgentBlogArticle: DobryAgentBlogArticle
  ): Observable<boolean> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        text: 'Czy na pewno chcesz usunąć artykuł?'
      },
    });

    return dialogRef.afterClosed()
      .pipe(
        take(1),
        switchMap((confirmed: boolean) => {
          if (!confirmed) {
            return of(false);
          }

          return this.delete(dobryAgentBlogArticle.uuid)
            .pipe(
              tap(() => {
                this.snack.open(
                  'Pomyślnie usunięto artykuł',
                  '',
                  {duration: 2000}
                );
              }),
              map(() => true)
            );
        })
      );
  }

  public confirmDeleteWeak(
    uuid: string
  ): Observable<boolean> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        text: 'Czy na pewno chcesz przenieść artykuł do archiwum?'
      },
    });

    return dialogRef.afterClosed()
      .pipe(
        take(1),
        switchMap((confirmed: boolean) => {
          if (!confirmed) {
            return of(false);
          }

          return this.deleteWeak(uuid)
            .pipe(
              tap(() => {
                this.snack.open(
                  'Pomyślnie przeniesiono artykuł do archiwum',
                  '',
                  {duration: 2000}
                );
              }),
              map(() => true)
            );
        })
      );
  }


  public confirmRestore(
    dobryAgentBlogArticle: DobryAgentBlogArticle
  ): Observable<boolean> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        text: 'Czy na pewno chcesz przywrócić artykuł z archiwum?'
      },
    });

    return dialogRef.afterClosed()
      .pipe(
        take(1),
        switchMap((confirmed: boolean) => {
          if (!confirmed) {
            return of(false);
          }

          return this.restore(dobryAgentBlogArticle.uuid)
            .pipe(
              tap(() => {
                this.snack.open(
                  'Pomyślnie przywrócono artykuł',
                  '',
                  {duration: 2000}
                );
              }),
              map(() => true)
            );
        })
      );
  }

}
